import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { usePermissions } from '../../hooks/usePermissions'
import { 
  Home, Users, Heart, Calendar, FileText, Settings, BarChart3, Building2, User, Globe, Shield, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Activity, Bell, CreditCard, QrCode, TrendingUp, CalendarDays, ClipboardList, Stethoscope
} from 'lucide-react'
import { ArtemisMascotMark, ArtemisWordmark } from '../brand'

// Navegación completa (para ADMIN, SUPERADMIN, VETERINARIO)
const fullNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Agenda hoy', href: '/agenda', icon: CalendarDays },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Plantillas', href: '/plantillas-consulta', icon: ClipboardList },
  { name: 'Operaciones', href: '/operaciones-clinica', icon: Stethoscope },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Clínicas', href: '/clinicas', icon: Building2 },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

// Navegación para SUPERADMIN (incluye Configuración y Usuarios)
const superAdminNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Agenda hoy', href: '/agenda', icon: CalendarDays },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Plantillas', href: '/plantillas-consulta', icon: ClipboardList },
  { name: 'Operaciones', href: '/operaciones-clinica', icon: Stethoscope },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
  { name: 'Usuarios', href: '/usuarios', icon: Users },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Clínicas', href: '/clinicas', icon: Building2 },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
  {
    name: 'Configuración',
    icon: Settings,
    children: [
      { name: 'Roles y Usuarios', href: '/configuracion/roles', icon: Shield },
      { name: 'Condiciones de Salud', href: '/configuracion/condiciones-salud', icon: Activity },
      { name: 'Push condición salud', href: '/configuracion/push-condicion-salud', icon: Bell },
      { name: 'SEO', href: '/configuracion/seo', icon: TrendingUp },
      { name: 'Países', href: '/configuracion/paises', icon: Globe },
      { name: 'Sistema', href: '/configuracion/sistema', icon: Settings },
    ]
  },
]

// Navegación restringida para DUEÑO (sin Reportes: backend solo permite ADMIN/SUPERADMIN/VETERINARIO)
const duenoNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Mis Suscripciones', href: '/suscripciones-clinicas', icon: CreditCard },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

// Navegación restringida para PET_SITTER (sin Reportes: mismo criterio backend)
const petSitterNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

// Navegación para VETERINARIO admin de clínica
const veterinarioAdminNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Agenda hoy', href: '/agenda', icon: CalendarDays },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Plantillas', href: '/plantillas-consulta', icon: ClipboardList },
  { name: 'Operaciones', href: '/operaciones-clinica', icon: Stethoscope },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Clínicas', href: '/clinicas', icon: Building2 },
  { name: 'Suscripciones Pendientes', href: '/suscripciones-pendientes', icon: CreditCard },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation()
  const { role, isVeterinarioAdmin } = usePermissions()
  
  // Determinar navegación según permisos (alineado con permissions.js)
  const navigation = useMemo(() => {
    if (role === 'DUEÑO') return duenoNavigation
    if (role === 'PET_SITTER') return petSitterNavigation
    if (role === 'SUPERADMIN') return superAdminNavigation
    if (role === 'VETERINARIO') {
      if (isVeterinarioAdmin) return veterinarioAdminNavigation
      return fullNavigation.filter(item => item.name !== 'Veterinarios')
    }
    return fullNavigation
  }, [role, isVeterinarioAdmin])
  
  // Abrir automáticamente el submenu si estamos en una de sus rutas
  const getInitialOpenSubmenus = () => {
    const open = new Set()
    if (location.pathname.startsWith('/configuracion')) {
      open.add('Configuración')
    }
    return open
  }
  
  const [openSubmenus, setOpenSubmenus] = useState(getInitialOpenSubmenus())

  useEffect(() => {
    if (location.pathname.startsWith('/configuracion')) {
      setOpenSubmenus(prev => new Set([...prev, 'Configuración']))
    }
  }, [location.pathname])

  const toggleSubmenu = (itemName) => {
    setOpenSubmenus(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemName)) {
        newSet.delete(itemName)
      } else {
        newSet.add(itemName)
      }
      return newSet
    })
  }

  const isItemActive = (item) => {
    if (item.href) {
      return location.pathname === item.href
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.href)
    }
    return false
  }

  const groupedNavigation = useMemo(() => {
    const mainItems = navigation.filter((item) => !['Notificaciones', 'Configuración'].includes(item.name))
    const utilityItems = navigation.filter((item) => ['Notificaciones', 'Configuración'].includes(item.name))

    return [
      { label: 'MENU', items: mainItems },
      { label: 'OTROS', items: utilityItems },
    ].filter((group) => group.items.length > 0)
  }, [navigation])

  return (
    <div className={`flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 ease-in-out ${collapsed ? 'w-16' : 'w-64'} h-full relative overflow-hidden`}>
      <div className="flex items-center justify-between gap-2 px-3 py-4 sm:px-4 sm:py-5 border-b border-gray-200 dark:border-gray-800 relative z-10">
        <div
          className={`flex items-center min-w-0 flex-1 ${collapsed ? 'justify-center' : ''}`}
        >
          {!collapsed ? (
            <ArtemisWordmark mascotSize={36} className="animate-fade-in" />
          ) : (
            <ArtemisMascotMark size={28} className="rounded-[8px] flex-shrink-0" />
          )}
        </div>
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-all duration-200 flex-shrink-0 active:scale-95"
          title={collapsed ? 'Expandir' : 'Colapsar'}
          aria-label={collapsed ? 'Expandir' : 'Colapsar'}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 transition-transform duration-300" />
          ) : (
            <ChevronLeft className="w-5 h-5 transition-transform duration-300" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto relative z-10">
        {groupedNavigation.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
                {group.label}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((item, index) => {
                const isActive = isItemActive(item)
                const hasChildren = item.children && item.children.length > 0
                const isSubmenuOpen = hasChildren && openSubmenus.has(item.name)

                return (
                  <div key={item.name} className="animate-fade-in-up" style={{ animationDelay: `${index * 35}ms` }}>
                    {hasChildren ? (
                      <div>
                        <button
                          onClick={() => toggleSubmenu(item.name)}
                          className={`group w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <div className="flex items-center space-x-3 min-w-0">
                            <item.icon className={`flex-shrink-0 w-5 h-5 transition-colors duration-200 ${isActive ? 'text-brand-600 dark:text-brand-300' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'}`} />
                            {!collapsed && <span className="truncate">{item.name}</span>}
                          </div>
                          {!collapsed && (
                            <div className="flex-shrink-0">
                              {isSubmenuOpen ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          )}
                        </button>

                        {!collapsed && isSubmenuOpen && (
                          <div className="ml-4 mt-2 space-y-1 border-l border-gray-200 dark:border-gray-800 pl-4">
                            {item.children.map((child) => {
                              const isChildActive = location.pathname === child.href
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                    isChildActive
                                      ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300'
                                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                  }`}
                                >
                                  <child.icon className={`flex-shrink-0 w-4 h-4 mr-3 ${isChildActive ? 'text-brand-600 dark:text-brand-300' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
                                  <span className="truncate">{child.name}</span>
                                </Link>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                        }`}
                        title={collapsed ? item.name : ''}
                      >
                        <item.icon className={`flex-shrink-0 w-5 h-5 transition-colors duration-200 ${collapsed ? 'mx-auto' : 'mr-3'} ${isActive ? 'text-brand-600 dark:text-brand-300' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'}`} />
                        {!collapsed && <span className="truncate">{item.name}</span>}
                      </Link>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-800">
          <div className="rounded-xl bg-gray-50 px-3 py-2 text-center text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            Artemis v1.0
          </div>
        </div>
      )}
    </div>
  )
}
